/**
 * Plain-English copy for the Independence step, and for the block reasons the
 * gate returns.
 *
 * Verbatim from `tests/fixtures/independence/INDEPENDENCE_FIELD_HELP.md`, which
 * was written against `core/attest/independence.py` line by line. Keeping the
 * strings in one module rather than inline in the JSX is what lets the same
 * sentences appear in two places that must agree: the "(i)" on the field the
 * CPA is filling in, and the refusal they get if they fill it in wrongly. Help
 * text that drifts from the gate is worse than no help text — it teaches the
 * wrong model of a professional obligation.
 */

export interface FieldHelp {
  /** Accessible name for the "(i)" — the field this help belongs to. */
  label: string
  /** "What it means", then the consequence. Rendered as separate paragraphs. */
  paragraphs: string[]
}

export const INDEPENDENCE_FIELD_HELP: Record<string, FieldHelp> = {
  signingIdentity: {
    label: 'This letter will be signed as',
    paragraphs: [
      'The signing firm, CPA, license number and address are pulled from your Firm Profile at the moment the letter is generated. They are shown here read-only and cannot be edited on an individual letter.',
      'To change them: edit your Firm Profile — the letter always reflects the profile as of generation time.',
    ],
  },
  peerReviewExpiry: {
    label: 'Peer review expires',
    paragraphs: [
      'The date your firm’s AICPA peer review lapses.',
      'If it is in the past, no letter can be issued — the eligibility gate reports “peer review expired <date>”. Renew before issuing.',
    ],
  },
  attestedNoImpairingInterest: {
    label: 'It holds no interest that impairs independence',
    paragraphs: [
      'You are affirming that the firm — and its covered members — hold no ownership stake, loan, officer/director/trustee role, close-family financial interest, contingent fee, or joint venture tied to the subject entity.',
      'If you leave it unticked (and declare nothing) the letter is blocked — “the issuer has not attested that it holds no impairing interest.” Silence is not a pass.',
      'Do not tick it to get past the screen if it isn’t true. Ticking this while an interest is actually on record is treated as a worse finding than a disclosed conflict — a false attestation. Declare the interest instead, below.',
    ],
  },
  licensedForAttest: {
    label: 'It is licensed to perform attest work in the engagement state',
    paragraphs: [
      'Your firm holds an active attest license (not just a CPA license) valid in the state where this engagement sits.',
      'If you leave it unticked: blocked — “firm is not licensed to perform attest engagements” (and, if the engagement state isn’t among your licensed states, “firm is not licensed in <ST>”).',
    ],
  },
  peerReviewCurrent: {
    label: 'Its peer review is current',
    paragraphs: [
      'Your firm’s mandatory AICPA peer review is current. See the expiry date above for the hard cutoff.',
      'If you leave it unticked: blocked — “firm has no current peer review.”',
    ],
  },
  isPlatformBackOffice: {
    label: 'This firm is the platform’s own back-office firm',
    paragraphs: [
      'Leave this UNTICKED. It is only for the platform’s internal bookkeeping firm.',
      'If ticked, the letter is blocked by design — the platform’s back-office firm may never issue an attest letter. Independent, peer-reviewed external CPA firms issue letters; the platform’s own firm never does.',
    ],
  },
  declaredInterests: {
    label: 'Declared interests',
    paragraphs: [
      'Disclose any tie the firm or its covered members have to the subject entity — ownership, a loan, an officer/director/trustee role, a close-family financial interest, a contingent fee, or a joint venture.',
      'Any interest in the subject entity blocks the letter, whatever its type — and a type the system doesn’t recognise is treated as impairing too. That is intended.',
      'Declare it anyway: an undisclosed interest is the worse outcome. If you have a real conflict, this letter simply isn’t yours to sign — route it to an independent firm.',
    ],
  },
}

/**
 * Engine code → what it actually means to the CPA reading the refusal.
 *
 * The engine's own `reason` string is accurate but written for the record, not
 * for the person who has to act on it. These sit alongside it, not instead of
 * it: the record keeps the exact wording, the CPA gets the sentence that tells
 * them what to do.
 */
export const INDEPENDENCE_CODE_MEANINGS: Record<string, string> = {
  NO_INDEPENDENCE_ATTESTATION:
    'You didn’t confirm the firm is free of impairing interests, and you didn’t declare one either. Tick the top box (if true) or declare the interest.',
  FINANCIAL_INTEREST:
    'An interest in the subject entity is on record, so this firm can’t issue the letter.',
  ATTESTATION_CONTRADICTED:
    'You said “no impairing interest,” but an interest in the subject is on record. A false attestation is more serious than a disclosed conflict — resolve it first.',
  PLATFORM_BACK_OFFICE_CANNOT_ISSUE:
    'This firm is flagged as the platform’s own back-office firm, which can never sign an attest letter. Route to an external independent firm.',
}
